import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Loader, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const IOTypesPage = () => {
  const [ioTypes, setIOTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchIOTypes();
  }, []);

  const fetchIOTypes = async () => {
    try {
      const response = await axios.get(`${API}/io-types`);
      setIOTypes(response.data);
    } catch (err) {
      console.error('Error fetching IO types:', err);
      setError('Ошибка загрузки типов ИО');
    } finally {
      setLoading(false);
    }
  };

  const getTypeColor = (id) => {
    const colors = {
      'DISINFORMATION': 'border-red-500 bg-red-50',
      'DEMORALIZATION': 'border-orange-500 bg-orange-50',
      'DISCREDITATION': 'border-yellow-500 bg-yellow-50',
      'INTIMIDATION': 'border-purple-500 bg-purple-50',
      'HATE_INCITEMENT': 'border-pink-500 bg-pink-50',
      'PANIC_CREATION': 'border-indigo-500 bg-indigo-50',
      'PROVOCATION': 'border-blue-500 bg-blue-50',
      'AUTHORITY_UNDERSCORE': 'border-cyan-500 bg-cyan-50'
    };
    return colors[id] || 'border-gray-500 bg-gray-50';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Загрузка типов информационных операций...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
          <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
          <p className="text-red-800 text-center">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-3" data-testid="io-types-title">
            Типы информационных операций
          </h1>
          <p className="text-lg text-gray-600">
            Полный справочник по классификации информационных операций
          </p>
        </div>

        {/* IO Types Grid */}
        <div className="space-y-6">
          {ioTypes.map((type, index) => (
            <div 
              key={type.id}
              className={`bg-white rounded-xl shadow-sm border-l-4 ${getTypeColor(type.id)} p-6`}
              data-testid={`io-type-card-${index}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-1">
                    {type.name_ru}
                  </h2>
                  <p className="text-sm text-gray-500 font-mono">{type.name}</p>
                </div>
              </div>

              <p className="text-gray-700 mb-6 text-lg">{type.description}</p>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Key Features */}
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                    Ключевые признаки
                  </h3>
                  <ul className="space-y-2">
                    {type.key_features.map((feature, idx) => (
                      <li key={idx} className="text-gray-700 text-sm flex items-start gap-2">
                        <span className="text-blue-500 mt-1">•</span>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Examples */}
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                    Примеры
                  </h3>
                  <ul className="space-y-2">
                    {type.examples.map((example, idx) => (
                      <li key={idx} className="text-gray-700 text-sm bg-gray-50 p-2 rounded italic">
                        "{example}"
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Not Include */}
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                      Не включать
                    </h3>
                    <ul className="space-y-1">
                      {type.not_include.map((item, idx) => (
                        <li key={idx} className="text-gray-700 text-sm flex items-start gap-2">
                          <span className="text-red-500 mt-1">✕</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Criterion */}
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                      Критерий
                    </h3>
                    <p className="text-gray-700 text-sm bg-purple-50 p-3 rounded">
                      {type.criterion}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default IOTypesPage;
